// src/pages/LoginPage.js - NIS DESIGN WITH MUI
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import nisLogo from '../assets/images/immigrationLogo.png';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    serviceNumber: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.serviceNumber.trim()) {
      setError('Service number is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }

    setLoading(true);

    try {
      await login(formData.serviceNumber, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f0f4f0',
        px: 2,
        py: 3,
        position: 'relative',
        // Subtle grid pattern background
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Outer green card (frame) */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: '#1e4d2b',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 4px 6px rgba(0,0,0,0.08), 0 20px 50px rgba(0,0,0,0.15)',
          }}
        >
          {/* Green header strip */}
          <Box sx={{ px: { xs: 4, sm: 6 }, pt: 3.5, pb: 2.5, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.95)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <Box
                component="img"
                src={nisLogo}
                alt="NIS Logo"
                sx={{ height: 40, width: 40, objectFit: 'contain' }}
              />
            </Box>
            <Typography
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                lineHeight: 1.4,
                letterSpacing: '0.02em',
              }}
            >
              Nigeria Immigration Service
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              Emolument Verification System
            </Typography>
          </Box>

          {/* White form body with padding on sides and bottom */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'white',
              }}
            >
              {/* Curved connector from green into white */}
              <Box sx={{ bgcolor: '#1e4d2b', height: 20 }}>
                <Box
                  sx={{
                    bgcolor: 'white',
                    height: 20,
                    borderRadius: '20px 20px 0 0',
                  }}
                />
              </Box>

              {/* Form content */}
              <Box sx={{ px: { xs: 4, sm: 6 }, pb: 3.5 }}>
                {/* Welcome text */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: 'rgb(17, 24, 39)', fontWeight: 'bold', fontSize: '1.25rem' }}
                  >
                    Welcome back
                  </Typography>
                  <Typography sx={{ color: 'rgb(156, 163, 175)', fontSize: '0.875rem', mt: 0.25 }}>
                    Sign in to access your account
                  </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                  {/* Service Number Field */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      component="label"
                      sx={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'rgb(55, 65, 81)',
                        mb: 0.75,
                      }}
                    >
                      Service Number
                    </Typography>
                    <TextField
                      fullWidth
                      name="serviceNumber"
                      value={formData.serviceNumber}
                      onChange={handleChange}
                      placeholder="e.g., NIS001234"
                      disabled={loading}
                      autoFocus
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ fontSize: 18, color: 'rgb(156, 163, 175)' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.875rem',
                          bgcolor: 'rgb(249, 250, 251)',
                          borderRadius: 3,
                          '& fieldset': {
                            borderColor: 'rgb(229, 231, 235)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgb(209, 213, 219)',
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            '& fieldset': {
                              borderColor: '#1e4d2b',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Password Field */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      component="label"
                      sx={{
                        display: 'block',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: 'rgb(55, 65, 81)',
                        mb: 0.75,
                      }}
                    >
                      Password
                    </Typography>
                    <TextField
                      fullWidth
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      disabled={loading}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ fontSize: 18, color: 'rgb(156, 163, 175)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{ color: 'rgb(156, 163, 175)' }}
                            >
                              {showPassword ? (
                                <VisibilityOff sx={{ fontSize: 18 }} />
                              ) : (
                                <Visibility sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.875rem',
                          bgcolor: 'rgb(249, 250, 251)',
                          borderRadius: 3,
                          '& fieldset': {
                            borderColor: 'rgb(229, 231, 235)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgb(209, 213, 219)',
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            '& fieldset': {
                              borderColor: '#1e4d2b',
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                    />
                  </Box>

                  {/* Error Alert */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 2,
                        bgcolor: 'rgb(254, 242, 242)',
                        border: '1px solid rgb(254, 226, 226)',
                        borderRadius: 3,
                        fontSize: '0.875rem',
                        '& .MuiAlert-icon': {
                          fontSize: 18,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.25,
                      px: 2,
                      borderRadius: 3,
                      bgcolor: '#1e4d2b',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: '#163d21',
                      },
                      '&:disabled': {
                        opacity: 0.6,
                        bgcolor: '#1e4d2b',
                        color: 'white',
                      },
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                        <span>Signing in...</span>
                      </Box>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                {/* Bottom note */}
                <Box
                  sx={{
                    mt: 2.5,
                    pt: 2,
                    borderTop: '1px solid rgb(243, 244, 246)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 14, color: '#1e4d2b' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgb(156, 163, 175)' }}>
                    Authorized personnel only
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Paper>

        {/* Footer */}
        <Typography
          sx={{
            textAlign: 'center',
            mt: 2,
            fontSize: '0.75rem',
            color: 'rgb(156, 163, 175)',
          }}
        >
          Personnel Verification System v1.0 · © 2025 Nigeria Immigration Service
        </Typography>
      </Container>
    </Box>
  );
};

export default LoginPage;